import {
  Anchor,
  Box,
  Button,
  COLOR_PALETTE,
  Flex,
  Icon,
  Popover,
  Stack,
  Text,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import { faList } from '@inflearn/pro-solid-svg-icons';
import { findLast, throttle } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { type MouseEvent, useCallback, useEffect, useState } from 'react';

import type { NoteWithUnitsSectionResponse } from '../../../../../resources/@types/uccSchema';
import useRouterQueryId from '../../../../hooks/useRouterQueryId';
import { GNB_DESKTOP_HEIGHT, INTEGRATED_GNB_HEIGHT } from '../../../../utils/size';
import useNoteIdWithUnitsQuery from '../hooks/queries/useNoteIdWithUnitsQuery';
import { ASIDE_TOP_MARGIN, PAGE_TOP_PADDING, SAFE_MARGIN } from '../utils/size';
import { generateSectionId } from '../utils/toc';

export default function ToC() {
  const { t } = useTranslation();
  const courseNoteId = useRouterQueryId();

  const { data } = useNoteIdWithUnitsQuery({ id: courseNoteId });
  const sections = data?.sections;

  const [activeSectionId, setActiveSectionId] = useState<number | null>(null);

  const findCurrentSectionIdByScrollPosition = useCallback((): number | null => {
    if (!sections || sections.length === 0) {
      return null;
    }

    const SAFE_SCROLL_MARGIN = SAFE_MARGIN + 20;

    const activeSection = findLast(sections, ({ id }) => {
      const sectionElement = document.getElementById(generateSectionId(id));
      const rect = sectionElement?.getBoundingClientRect();

      return rect ? rect.top <= SAFE_SCROLL_MARGIN : false;
    });

    return activeSection?.id ?? sections[0].id;
  }, [sections]);

  const scrollToSectionFromHash = useCallback(() => {
    const { hash } = window.location;

    if (hash) {
      const sectionId = hash.replace('#', '');
      const sectionElement = document.getElementById(sectionId);

      if (sectionElement) {
        setTimeout(() => {
          window.scrollTo({
            top: sectionElement.offsetTop - SAFE_MARGIN,
          });
        }, 0);
      }
    }
  }, []);

  const replaceUrlWithHash = (sectionId: number) =>
    window.history.replaceState(null, '', `#${generateSectionId(sectionId)}`);

  const handleClickToCItem = (sectionId: number) => {
    replaceUrlWithHash(sectionId);
    scrollToSectionFromHash();
  };

  // 페이지 로드 시 URL hash로 스크롤 위치 이동
  useEffect(() => {
    scrollToSectionFromHash();
  }, [scrollToSectionFromHash]);

  // 스크롤 위치에 따라 ToC item 활성화
  useEffect(() => {
    const handleScroll = throttle(() => {
      setActiveSectionId(findCurrentSectionIdByScrollPosition());
    }, 100);

    setActiveSectionId(findCurrentSectionIdByScrollPosition());
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [findCurrentSectionIdByScrollPosition, scrollToSectionFromHash]);

  if (!sections) {
    return null;
  }

  return (
    <>
      {/* 데스크탑 UI */}
      <Box
        css={{
          display: 'block',
          [desktopFirstMediaQuery.desktop]: { display: 'none' },
        }}
      >
        <ToCItems
          sections={sections}
          onClickToCItem={handleClickToCItem}
          activeSectionId={activeSectionId}
        />
      </Box>
      {/* 모바일 UI */}
      <Box
        css={{
          display: 'none',
          [desktopFirstMediaQuery.desktop]: { display: 'block' },
        }}
      >
        <Popover
          width={232}
          withinPortal
          position="bottom-start"
          shadow="sm"
          styles={{
            dropdown: {
              display: 'none',
              [desktopFirstMediaQuery.desktop]: { display: 'block' },
            },
          }}
        >
          <Popover.Target>
            <Button variant="subtle" color="gray" size="compact-sm" radius="md">
              <Flex gap={6} align="center">
                <Icon icon={faList} size="sm" />
                <Text>{t('이 노트의 섹션')}</Text>
              </Flex>
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <ToCItems
              sections={sections}
              onClickToCItem={handleClickToCItem}
              activeSectionId={activeSectionId}
            />
          </Popover.Dropdown>
        </Popover>
      </Box>
    </>
  );
}

function ToCItems({
  sections,
  activeSectionId,
  onClickToCItem,
}: {
  sections: NoteWithUnitsSectionResponse[];
  activeSectionId: number | null;
  onClickToCItem: (sectionId: number) => void;
}) {
  return (
    <Stack
      component="ul"
      w="100%"
      styles={{
        root: {
          maxHeight: `calc(100vh - ${
            INTEGRATED_GNB_HEIGHT +
            GNB_DESKTOP_HEIGHT +
            PAGE_TOP_PADDING +
            ASIDE_TOP_MARGIN +
            SAFE_MARGIN * 2
          }px)`,
          overflowY: 'auto',
        },
      }}
    >
      {sections.map((section) => {
        const isActive = activeSectionId === section.id;

        return (
          <Box component="li" key={section.id}>
            <Anchor
              href={`#${generateSectionId(section.id)}`}
              pl={12}
              py={4}
              c={isActive ? 'dark' : 'gray'}
              fw={isActive ? 500 : 400}
              size="sm"
              styles={{
                root: {
                  display: 'block',
                  borderLeft: `2px solid ${COLOR_PALETTE.gray[2]}`,
                  ...(isActive && {
                    borderColor: COLOR_PALETTE.gray[6],
                  }),
                },
              }}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();
                onClickToCItem(section.id);
              }}
            >
              {section.title}
            </Anchor>
          </Box>
        );
      })}
    </Stack>
  );
}
